<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import {
    computed, ref, onMounted, watch,
} from 'vue';
import Checkbox from 'primevue/checkbox';
import Popover from 'primevue/popover';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import ToggleSwitch from 'primevue/toggleswitch';
import Draggable from 'vuedraggable';
import { setCookie, getCookieJSON } from '@/utils/cookieUtils';
import {
    normalizeTableColumnSettings,
    type ColumnCustomizeOption,
    type TableColumnSettings,
} from '@/composables/useTableColumns';
import { useTableColumnPreferencesStore } from '@/stores/tableColumnPreferences';
import {
    TableColumnPreferenceId,
    type TableColumnPreferenceIdValue,
} from '@/services/tableColumnPreferenceService';
import IconDrag from '@/components/icons/IconDrag.vue';
import IconSettings from '@/components/icons/IconSettings.vue';
import ToolbarIconButton from './ToolbarIconButton.vue';

type PersistenceMode = 'cookie' | 'account';
type PersistenceAction = 'save' | 'reset';

interface Props {
    columns: ColumnCustomizeOption[];
    visibleColumnKeys: string[];
    columnOrder: string[];
    lockedColumnKeys?: string[];
    disabledColumnKeys?: string[];
    tableId?: string;
    persistence?: PersistenceMode;
    disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    lockedColumnKeys: () => [],
    disabledColumnKeys: () => [],
    tableId: undefined,
    persistence: 'cookie',
    disabled: false,
});

const emit = defineEmits<{
    'update:visibleColumnKeys': [value: string[]];
    'update:columnOrder': [value: string[]];
    persistenceError: [action: PersistenceAction, error: unknown];
}>();

const COOKIE_PREFIX = 'table_columns_';
const COOKIE_EXPIRY_DAYS = 365;

interface ToolbarIconButtonInstance {
    buttonEl: HTMLButtonElement | null;
}

interface PopoverInstance {
    visible?: boolean;
    toggle: (event: MouseEvent, target?: HTMLElement | null) => void;
    hide: () => void;
}

interface DraggableMoveEvent {
    draggedContext: {
        element: string;
    };
}

const preferenceStore = useTableColumnPreferencesStore();
const isAccountPersistence = computed(() => props.persistence === 'account');

function getCookieName(): string | null {
    return props.tableId ? `${COOKIE_PREFIX}${props.tableId}` : null;
}

function saveSettingsToCookie(settings: TableColumnSettings): void {
    const cookieName = getCookieName();
    if (!cookieName) return;
    try {
        setCookie(
            cookieName,
            settings as unknown as Record<string, unknown>,
            { days: COOKIE_EXPIRY_DAYS },
        );
    } catch (e) {
        console.warn('Failed to save column settings to cookie:', e);
    }
}

function loadSettingsFromCookie(): TableColumnSettings | null {
    const cookieName = getCookieName();
    if (!cookieName) return null;
    try {
        return getCookieJSON<TableColumnSettings>(cookieName);
    } catch (e) {
        console.warn('Failed to load column settings from cookie:', e);
        return null;
    }
}

const defaultColumnKeys = computed<string[]>(() => props.columns.map((c) => c.key));
const lockedColumnKeys = computed<string[]>(() => props.lockedColumnKeys);
const disabledColumnKeys = computed<string[]>(() => props.disabledColumnKeys);

function buildLabelMap(columns: ColumnCustomizeOption[]): Record<string, string> {
    return columns.reduce<Record<string, string>>((acc, col) => {
        acc[col.key] = col.label;
        return acc;
    }, {});
}

const labelByKey = computed<Record<string, string>>(() => buildLabelMap(props.columns));

const triggerRef = ref<ToolbarIconButtonInstance | null>(null);
const popoverRef = ref<PopoverInstance | null>(null);

const draftVisibleColumnKeys = ref<string[]>([]);
const draftColumnOrder = ref<string[]>([]);
const draftOnlyShowSelected = ref(false);
const draftSearch = ref('');

const totalColumns = computed(() => defaultColumnKeys.value.filter(
    (key) => !disabledColumnKeys.value.includes(key),
).length);
const draftVisibleCount = computed(() => draftVisibleColumnKeys.value.filter(
    (key) => !disabledColumnKeys.value.includes(key),
).length);
const accountBusy = computed(() => preferenceStore.saving || preferenceStore.resetting);
const triggerDisabled = computed(() => props.disabled || accountBusy.value);

const selectableColumnKeys = computed(() => (
    defaultColumnKeys.value.filter((key) => (
        !lockedColumnKeys.value.includes(key) && !disabledColumnKeys.value.includes(key)
    ))
));

function ensureLockedVisible(keys: string[]): string[] {
    const next = [...keys];
    lockedColumnKeys.value.forEach((lockedKey) => {
        if (!lockedKey) return;
        if (next.indexOf(lockedKey) !== -1) return;
        next.push(lockedKey);
    });
    const unique: string[] = [];
    next.forEach((key) => {
        if (!key) return;
        if (unique.indexOf(key) !== -1) return;
        unique.push(key);
    });
    return unique;
}

const isDraftSelected = (key: string) => draftVisibleColumnKeys.value.includes(key);
const isLockedColumn = (key: string) => lockedColumnKeys.value.includes(key);
const isDisabledColumn = (key: string) => disabledColumnKeys.value.includes(key);

const filteredDraftKeys = computed(() => {
    const query = draftSearch.value.trim().toLowerCase();
    const baseKeys = draftOnlyShowSelected.value
        ? draftColumnOrder.value.filter((key) => (
            isDraftSelected(key) && !isDisabledColumn(key)
        ))
        : draftColumnOrder.value;

    if (!query) return baseKeys;

    return baseKeys.filter((key) => {
        const label = labelByKey.value[key] ?? key;
        return label.toLowerCase().includes(query);
    });
});

const selectAllState = computed<'none' | 'partial' | 'all'>(() => {
    const selectedCount = selectableColumnKeys.value.filter(isDraftSelected).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === selectableColumnKeys.value.length) return 'all';
    return 'partial';
});

const selectAllIndeterminate = computed(() => selectAllState.value === 'partial');

const selectAllModel = computed<boolean>({
    get: () => selectAllState.value === 'all',
    set: (checked) => {
        const preservedDisabled = draftVisibleColumnKeys.value.filter(isDisabledColumn);
        draftVisibleColumnKeys.value = ensureLockedVisible(checked
            ? [...preservedDisabled, ...selectableColumnKeys.value]
            : preservedDisabled);
    },
});

const normalizedDraft = computed(() => normalizeTableColumnSettings(
    {
        visibleColumnKeys: draftVisibleColumnKeys.value,
        columnOrder: draftColumnOrder.value,
    },
    props.columns,
    lockedColumnKeys.value,
));

const normalizedSaved = computed(() => normalizeTableColumnSettings(
    {
        visibleColumnKeys: props.visibleColumnKeys,
        columnOrder: props.columnOrder,
    },
    props.columns,
    lockedColumnKeys.value,
));

const isDraftDirty = computed(() => (
    JSON.stringify(normalizedDraft.value) !== JSON.stringify(normalizedSaved.value)
));

function syncDraftFromSaved() {
    draftVisibleColumnKeys.value = normalizedSaved.value.visibleColumnKeys;
    draftColumnOrder.value = normalizedSaved.value.columnOrder;
    draftOnlyShowSelected.value = false;
    draftSearch.value = '';
}

// The draft is refreshed before the panel opens rather than on the popover's show event, so the
// first frame it renders already holds the saved columns instead of an empty list.
function onTriggerClick(event: MouseEvent) {
    if (triggerDisabled.value) return;
    if (!popoverRef.value?.visible) {
        syncDraftFromSaved();
    }

    popoverRef.value?.toggle(event, triggerRef.value?.buttonEl);
}

onMounted(() => {
    if (isAccountPersistence.value) return;

    const savedSettings = loadSettingsFromCookie();
    if (savedSettings) {
        const normalized = normalizeTableColumnSettings(
            savedSettings,
            props.columns,
            lockedColumnKeys.value,
        );

        if (JSON.stringify(normalized.visibleColumnKeys) !== JSON.stringify(props.visibleColumnKeys)) {
            emit('update:visibleColumnKeys', normalized.visibleColumnKeys);
        }
        if (JSON.stringify(normalized.columnOrder) !== JSON.stringify(props.columnOrder)) {
            emit('update:columnOrder', normalized.columnOrder);
        }
    }
});

function applyCookieChanges() {
    const { visibleColumnKeys, columnOrder } = normalizedDraft.value;

    saveSettingsToCookie({ visibleColumnKeys, columnOrder });

    emit('update:visibleColumnKeys', visibleColumnKeys);
    emit('update:columnOrder', columnOrder);
}

function getAccountTableId(): TableColumnPreferenceIdValue {
    if (props.tableId !== TableColumnPreferenceId.PAYMENTS) {
        throw new Error('Account column persistence requires a supported table id.');
    }
    return props.tableId;
}

async function saveAccountSettings() {
    if (!isDraftDirty.value || accountBusy.value) return;

    try {
        await preferenceStore.save(getAccountTableId(), normalizedDraft.value);
        popoverRef.value?.hide();
    } catch (error) {
        emit('persistenceError', 'save', error);
    }
}

async function resetToDefaults() {
    if (isAccountPersistence.value) {
        if (accountBusy.value) return;
        try {
            await preferenceStore.reset(getAccountTableId());
            popoverRef.value?.hide();
        } catch (error) {
            emit('persistenceError', 'reset', error);
        }
        return;
    }

    draftVisibleColumnKeys.value = ensureLockedVisible([...defaultColumnKeys.value]);
    draftColumnOrder.value = [...defaultColumnKeys.value];
}

function cancelAccountSettings() {
    syncDraftFromSaved();
    popoverRef.value?.hide();
}

function canMoveDraftColumn(event: DraggableMoveEvent): boolean {
    return !isDisabledColumn(event.draggedContext.element);
}

watch(draftVisibleColumnKeys, () => {
    if (!isAccountPersistence.value) applyCookieChanges();
}, { deep: true });
watch(draftColumnOrder, () => {
    if (!isAccountPersistence.value) applyCookieChanges();
}, { deep: true });
</script>

<template>
    <div class="col-customize">
        <ToolbarIconButton
            ref="triggerRef"
            aria-label="Customize table columns"
            :disabled="triggerDisabled"
            @click="onTriggerClick"
        >
            <IconSettings />
        </ToolbarIconButton>

        <Popover
            ref="popoverRef"
            :pt="{ root: { class: 'col-customize-popover popover-align-end popover-no-flip' } }"
        >
            <div
                class="col-customize__panel"
                role="dialog"
                aria-label="Customize table columns"
            >
                <div class="col-customize__header">
                    <div class="col-customize__title-row">
                        <div class="col-customize__title">Columns</div>
                        <button
                            type="button"
                            class="col-customize__reset"
                            :disabled="accountBusy"
                            @click="resetToDefaults"
                        >
                            {{ isAccountPersistence ? 'Reset to Default' : 'Reset' }}
                        </button>
                    </div>
                    <div class="col-customize__subtitle">
                        Displaying {{ draftVisibleCount }} of {{ totalColumns }} columns
                    </div>
                </div>

                <div class="col-customize__divider"></div>

                <label class="col-customize__toggle" for="col-only-selected-toggle">
                    <ToggleSwitch
                        inputId="col-only-selected-toggle"
                        v-model="draftOnlyShowSelected"
                    />
                    <span class="col-customize__toggle-label">Only Show Selected Columns</span>
                </label>

                <div class="col-customize__divider"></div>

                <IconField class="col-customize__search">
                    <InputText
                        v-model="draftSearch"
                        class="col-customize__search-field"
                        type="text"
                        name="column-search"
                        autocomplete="off"
                        placeholder="Search for a Column..."
                    />
                    <InputIcon
                        v-if="draftSearch"
                        class="pi pi-times"
                        @click="draftSearch = ''"
                    />
                </IconField>

                <div class="col-customize__divider"></div>

                <div
                    v-if="!draftSearch"
                    class="col-customize__selectall"
                >
                    <span class="col-customize__selectall-spacer" aria-hidden="true"></span>
                    <Checkbox
                        inputId="col-select-all"
                        v-model="selectAllModel"
                        binary
                        :indeterminate="selectAllIndeterminate"
                    />
                    <label class="col-customize__selectall-label" for="col-select-all">
                        Select All
                    </label>
                </div>

                <div class="col-customize__fields">
                    <Draggable
                        v-if="!draftSearch && !draftOnlyShowSelected"
                        v-model="draftColumnOrder"
                        tag="ul"
                        item-key="identity"
                        class="col-customize__list"
                        handle=".col-customize__drag"
                        :animation="200"
                        :move="canMoveDraftColumn"
                        ghost-class="col-customize__item--ghost"
                        chosen-class="col-customize__item--chosen"
                    >
                        <template #item="{ element: key }">
                            <li
                                class="col-customize__item"
                                :data-locked="isLockedColumn(key)"
                                :data-disabled="isDisabledColumn(key)"
                            >
                                <span
                                    v-if="isDisabledColumn(key)"
                                    class="pi pi-lock col-customize__lock"
                                    role="img"
                                    :aria-label="`${labelByKey[key] ?? key} is unavailable on this tab`"
                                ></span>
                                <IconDrag v-else class="col-customize__drag" />
                                <Checkbox
                                    v-if="isDisabledColumn(key)"
                                    :inputId="`col-toggle-${key}`"
                                    :modelValue="false"
                                    binary
                                    disabled
                                />
                                <Checkbox
                                    v-else
                                    :inputId="`col-toggle-${key}`"
                                    v-model="draftVisibleColumnKeys"
                                    :value="key"
                                    :disabled="isLockedColumn(key)"
                                    @click.stop
                                />
                                <label
                                    class="col-customize__item-label"
                                    :for="`col-toggle-${key}`"
                                >
                                    {{ labelByKey[key] ?? key }}
                                </label>
                            </li>
                        </template>
                    </Draggable>

                    <ul v-else class="col-customize__list">
                        <li
                            v-for="key in filteredDraftKeys"
                            :key="key"
                            class="col-customize__item"
                            :data-locked="isLockedColumn(key)"
                            :data-disabled="isDisabledColumn(key)"
                        >
                            <span
                                v-if="isDisabledColumn(key)"
                                class="pi pi-lock col-customize__lock"
                                role="img"
                                :aria-label="`${labelByKey[key] ?? key} is unavailable on this tab`"
                            ></span>
                            <IconDrag v-else class="col-customize__drag" />
                            <Checkbox
                                v-if="isDisabledColumn(key)"
                                :inputId="`col-toggle-${key}`"
                                :modelValue="false"
                                binary
                                disabled
                            />
                            <Checkbox
                                v-else
                                :inputId="`col-toggle-${key}`"
                                v-model="draftVisibleColumnKeys"
                                :value="key"
                                :disabled="isLockedColumn(key)"
                                @click.stop
                            />
                            <label
                                class="col-customize__item-label"
                                :for="`col-toggle-${key}`"
                            >
                                {{ labelByKey[key] ?? key }}
                            </label>
                        </li>
                    </ul>
                </div>

                <div v-if="isAccountPersistence" class="col-customize__footer">
                    <button
                        type="button"
                        class="col-customize__footer-button col-customize__footer-button--cancel"
                        :disabled="accountBusy"
                        @click="cancelAccountSettings"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="col-customize__footer-button col-customize__footer-button--apply"
                        :disabled="!isDraftDirty || accountBusy"
                        @click="saveAccountSettings"
                    >
                        {{ preferenceStore.saving ? 'Applying...' : 'Apply' }}
                    </button>
                </div>
            </div>
        </Popover>
    </div>
</template>

<style lang="scss" scoped>
.col-customize {
    position: relative;
    display: inline-flex;
}

:global(.p-popover.col-customize-popover) {
    --p-popover-gutter: 14px;
    --p-popover-content-padding: 0;
    background: transparent;
    border: 0 none;
    box-shadow: none;
}

:global(.p-popover.col-customize-popover::before),
:global(.p-popover.col-customize-popover::after) {
    display: none !important;
}

.col-customize__panel {
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 20px;
    width: 250px;
    background: $white-color;
    border: 1px solid $border-gray-color;
    border-radius: 10px;
    box-shadow: 8px 10px 50px rgba(0, 0, 0, 0.05);
}

.col-customize__header {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.col-customize__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.col-customize__reset {
    padding: 0;
    background: none;
    border: 0 none;
    cursor: pointer;
    font-family: $font-family-archivo;
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    color: $main-orange;

    &:hover {
        color: $main-orange-hover;
    }
}

.col-customize__divider {
    height: 2px;
    background: $border-gray-color;
    flex-shrink: 0;
}

.col-customize__title {
    font-family: $font-family-archivo;
    font-weight: 400;
    font-size: 16px;
    line-height: 18px;
    color: $black-color;
}

.col-customize__subtitle {
    font-family: $font-family-archivo;
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    color: rgba($black-color, 0.7);
}

.col-customize__toggle {
    display: flex;
    align-items: center;
    width: 100%;
    cursor: pointer;
}

.col-customize__toggle-label {
    font-family: $font-family-archivo;
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    color: rgba($black-color, 0.7);
}

.col-customize__search {
    width: 100%;

    :deep(.p-icon-field),
    :deep(.p-iconfield) {
        width: 100%;
    }
}

:deep(.p-inputtext) {
    width: 100%;
    background: $white-color !important;
    border: 1px solid $border-gray-color !important;
    border-radius: 20px;
    box-shadow: none !important;
    font-family: $font-family-archivo;
    font-size: 12px;
    font-weight: 400;
    color: rgba($black-color, 0.7);
    caret-color: $black-color;
    padding: 11px 20px !important;

    &::placeholder {
        color: rgba($black-color, 0.7);
    }
}

:deep(.p-inputtext:focus) {
    background: $white-color !important;
    border-color: $main-orange !important;
    border-radius: 20px;
    box-shadow: none !important;
}

:deep(.p-inputicon.pi-times) {
    cursor: pointer;
}

.col-customize__selectall {
    display: flex;
    align-items: center;
    gap: 0;
    cursor: pointer;

    :deep(.p-checkbox) {
        margin-right: 12px;
    }
}

.col-customize__selectall-spacer {
    flex: none;
    width: 14px;
    margin-right: 10px;
}

.col-customize__selectall-label {
    font-family: $font-family-archivo;
    font-weight: 400;
    font-size: 15px;
    line-height: 16px;
    color: $black-color;
    cursor: pointer;
}

.col-customize__fields {
    width: 100%;
}

.col-customize__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 0;
    padding: 0;
    list-style: none;
    width: 100%;
}

.col-customize__item {
    display: flex;
    align-items: center;
    gap: 0;
    width: 100%;

    &[data-locked='true'] {
        opacity: 0.5;
    }

    &[data-disabled='true'] {
        opacity: 0.5;
    }

    :deep(.p-checkbox) {
        margin-right: 12px;
    }
}

.col-customize__drag {
    flex: none;
    cursor: grab;
    margin-right: 10px;
}

.col-customize__lock {
    flex: none;
    width: 14px;
    margin-right: 10px;
    color: rgba($black-color, 0.6);
    font-size: 12px;
    text-align: center;
}

.col-customize__item-label {
    font-family: $font-family-archivo;
    font-weight: 400;
    font-size: 15px;
    line-height: 16px;
    color: $black-color;
    cursor: pointer;
    user-select: none;
}

.col-customize__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 15px;
    border-top: 2px solid $border-gray-color;
}

.col-customize__footer-button {
    min-width: 76px;
    height: 34px;
    padding: 0 14px;
    border-radius: 999px;
    cursor: pointer;
    font-family: $font-family-archivo;
    font-size: 14px;
    font-weight: 500;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }

    &--cancel {
        background: $white-color;
        border: 1px solid $border-gray-color;
        color: rgba($black-color, 0.72);

        &:hover:not(:disabled) {
            background: rgba($black-color, 0.03);
        }
    }

    &--apply {
        background: $main-orange;
        border: 1px solid transparent;
        color: $white-color;

        &:hover:not(:disabled) {
            background: $main-orange-hover;
        }
    }
}

/* ToggleSwitch styling - scale down to match design */
:deep(.p-toggleswitch) {
    transform: scale(0.75);
    transform-origin: left center;
}

/* Checkbox styling */
:deep(.p-checkbox),
:deep(.p-checkbox-box) {
    width: 16px !important;
    height: 16px !important;
}

:deep(.p-checkbox-box) {
    border-radius: 3px !important;
    background: $white-color !important;
    border: 1px solid rgba($black-color, 0.3) !important;
    box-shadow: none !important;
}

:deep(.p-checkbox-icon) {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
}

:deep(.p-checkbox-checked .p-checkbox-box),
:deep(.p-checkbox-checked.p-variant-filled .p-checkbox-box) {
    background: $main-orange !important;
    border-color: $main-orange !important;
}

:deep(.p-checkbox:not(.p-disabled):hover .p-checkbox-box) {
    border-color: $main-orange !important;
}

:deep(.p-checkbox-checked:not(.p-disabled):hover .p-checkbox-box) {
    background: $main-orange-hover !important;
    border-color: $main-orange-hover !important;
}

:deep(.p-checkbox-checked .p-checkbox-icon) {
    color: $white-color !important;
}

:deep(.p-checkbox[data-p-indeterminate='true'] .p-checkbox-box) {
    background: rgba($black-color, 0.7) !important;
    border-color: rgba($black-color, 0.7) !important;
}

:deep(.p-checkbox[data-p-indeterminate='true'] .p-checkbox-icon) {
    color: $white-color !important;
    width: 12px !important;
    height: 12px !important;
}

:deep(.p-checkbox[data-p-indeterminate='true']:not(.p-disabled):hover .p-checkbox-box) {
    background: rgba($black-color, 0.8) !important;
    border-color: rgba($black-color, 0.8) !important;
}

.col-customize__item[data-locked='true'] :deep(.p-checkbox-box),
.col-customize__item[data-disabled='true'] :deep(.p-checkbox-box) {
    background: rgba($black-color, 0.05) !important;
    border-color: $border-gray-color !important;
}

.col-customize__item[data-locked='true'] :deep(.p-checkbox-checked .p-checkbox-box),
.col-customize__item[data-disabled='true'] :deep(.p-checkbox-checked .p-checkbox-box) {
    background: rgba($black-color, 0.05) !important;
    border-color: $border-gray-color !important;
}

.col-customize__item[data-locked='true'] :deep(.p-checkbox-icon),
.col-customize__item[data-disabled='true'] :deep(.p-checkbox-icon) {
    color: rgba($black-color, 0.4) !important;
}

.col-customize__item--ghost {
    opacity: 0.4;
    background: rgba($main-orange, 0.06);
    border-radius: 6px;
}

.col-customize__item--chosen {
    background: rgba($main-orange, 0.04);
    border-radius: 6px;
}
</style>
